import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import apiConfig from '../../config/api.config';
import { Leaf, Save, ArrowRight, AlertCircle, CheckCircle, Fuel, Truck, Calculator } from 'lucide-react';

const SustainabilityContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  padding: ${({ theme }) => theme.spacing.lg};
`;

const SustainabilityCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const LogoIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const Section = styled.div`
  background: ${({ theme }) => theme.colors.gray50};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div<{ $fullWidth?: boolean }>`
  position: relative;
  grid-column: ${({ $fullWidth }) => $fullWidth ? '1 / -1' : 'auto'};
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.success};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.success}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.success};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.success}20;
  }
`;

const ResultsSection = styled.div`
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 2px solid ${({ theme }) => theme.colors.success};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const ResultItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.success}30;

  &:last-child {
    border-bottom: none;
  }
`;

const ResultLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ResultValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ClassificationBadge = styled.span<{ classification: string }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  
  ${({ classification, theme }) => {
    switch (classification) {
      case 'EXCELENTE':
        return `background: #10b981; color: white;`;
      case 'BUENO':
        return `background: #059669; color: white;`;
      case 'REGULAR':
        return `background: #f59e0b; color: white;`;
      case 'DEFICIENTE':
        return `background: #ef4444; color: white;`;
      default:
        return `background: ${theme.colors.gray200}; color: ${theme.colors.textSecondary};`;
    }
  }}
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};

  ${({ variant, theme }) => {
    if (variant === 'secondary') {
      return `
        background: ${theme.colors.gray100};
        color: ${theme.colors.textSecondary};
        
        &:hover {
          background: ${theme.colors.gray200};
        }
      `;
    }
    return `
      background: ${theme.colors.success};
      color: ${theme.colors.white};
      
      &:hover {
        background: #059669;
      }
    `;
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ type: 'error' | 'success' }>`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  ${({ type, theme }) => {
    if (type === 'error') {
      return `
        background: #fef2f2;
        color: #dc2626;
        border: 1px solid #fecaca;
      `;
    }
    return `
      background: #f0fdf4;
      color: #16a34a;
      border: 1px solid #bbf7d0;
    `;
  }}
`;

interface SustainabilityFormProps {
  onComplete?: () => void;
  onSkip?: () => void;
  userData?: { username: string; password: string };
}

const SustainabilityForm: React.FC<SustainabilityFormProps> = ({ onComplete, onSkip, userData }) => {
  const [formData, setFormData] = useState({
    consumo_galones: '',
    tipo_combustible: 'DIESEL',
    kilometraje_anual: '',
    numero_vehiculos: '',
    emisiones_co2_anual: ''
  });
  
  const [noAplica, setNoAplica] = useState(false); // Estado para "No aplica"
  const [calculatedResults, setCalculatedResults] = useState<any>(null);
  const [message, setMessage] = useState<{ type: 'error' | 'success', text: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const { login } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Calcular emisiones automáticamente basado en consumo y tipo de combustible
  const calculateEmissions = () => {
    const consumo = parseFloat(formData.consumo_galones) || 0;
    let factorEmision = 0;
    
    // Factores de emisión aproximados (kg CO2 por galón)
    switch (formData.tipo_combustible) {
      case 'DIESEL':
        factorEmision = 10.21;
        break;
      case 'GASOLINA':
        factorEmision = 8.89;
        break;
      case 'ELECTRICO':
        factorEmision = 0.52;
        break;
      case 'OTRO':
        factorEmision = 9.0; // Promedio
        break;
    }
    
    const emisionesToneladas = (consumo * factorEmision) / 1000;
    return emisionesToneladas;
  };

  // Efecto para calcular emisiones automáticamente
  useEffect(() => {
    if (formData.consumo_galones && formData.tipo_combustible) {
      const calculatedEmissions = calculateEmissions();
      setFormData(prev => ({
        ...prev,
        emisiones_co2_anual: calculatedEmissions.toFixed(3)
      }));
    }
  }, [formData.consumo_galones, formData.tipo_combustible]);

  // Calcular KPIs en tiempo real
  useEffect(() => {
    const consumo = parseFloat(formData.consumo_galones) || 0;
    const kilometraje = parseFloat(formData.kilometraje_anual) || 0;
    const vehiculos = parseInt(formData.numero_vehiculos) || 0;
    const emisiones = parseFloat(formData.emisiones_co2_anual) || 0;

    if (emisiones > 0) {
      const kpiEmisionesPorKm = kilometraje > 0 ? (emisiones * 1000) / kilometraje : null;
      const kpiEmisionesPorVehiculo = vehiculos > 0 ? emisiones / vehiculos : null;
      
      // Calcular puntaje de sostenibilidad
      let puntaje = null;
      let clasificacion = 'SIN_EVALUAR';
      
      if (kpiEmisionesPorKm !== null) {
        if (kpiEmisionesPorKm <= 0.50) puntaje = 100;
        else if (kpiEmisionesPorKm <= 0.80) puntaje = 80;
        else if (kpiEmisionesPorKm <= 1.20) puntaje = 60;
        else if (kpiEmisionesPorKm <= 2.00) puntaje = 40;
        else puntaje = 20;
        
        if (puntaje >= 80) clasificacion = 'EXCELENTE';
        else if (puntaje >= 60) clasificacion = 'BUENO';
        else if (puntaje >= 40) clasificacion = 'REGULAR';
        else clasificacion = 'DEFICIENTE';
      }

      setCalculatedResults({
        kpi_emisiones_por_km: kpiEmisionesPorKm,
        kpi_emisiones_por_vehiculo: kpiEmisionesPorVehiculo,
        puntaje_sostenibilidad: puntaje,
        clasificacion_sostenibilidad: clasificacion
      });
    } else {
      setCalculatedResults(null);
    }
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setIsLoading(true);

    try {
      // Si NO está marcado "No aplica", validar campos requeridos
      if (!noAplica && (!formData.consumo_galones || !formData.kilometraje_anual || !formData.numero_vehiculos)) {
        throw new Error('Por favor complete todos los campos requeridos o marque "No aplica"');
      }

      // Si tenemos datos de usuario, hacer login primero
      let authToken = null;
      if (userData && userData.username && userData.password) {
        try {
          await login(userData.username, userData.password);
          // Obtener el token del localStorage después del login
          authToken = localStorage.getItem('auth_token');
        } catch (loginError) {
          console.error('Error en login automático:', loginError);
          throw new Error('Error al autenticar usuario para guardar sostenibilidad');
        }
      } else {
        // Si ya está autenticado, usar token existente
        authToken = localStorage.getItem('auth_token');
      }

      if (!authToken) {
        throw new Error('No se pudo obtener token de autenticación');
      }

      // Preparar datos para enviar
      const dataToSend = noAplica ? {
        consumo_galones: 0,
        tipo_combustible: 'NO_APLICA',
        kilometraje_anual: 0,
        numero_vehiculos: 0,
        emisiones_co2_anual: 0,
        no_aplica: true
      } : {
        consumo_galones: parseFloat(formData.consumo_galones),
        tipo_combustible: formData.tipo_combustible,
        kilometraje_anual: parseFloat(formData.kilometraje_anual),
        numero_vehiculos: parseInt(formData.numero_vehiculos),
        emisiones_co2_anual: parseFloat(formData.emisiones_co2_anual),
        no_aplica: false
      };

      const response = await fetch(apiConfig.endpoints.huellaCarbono, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(dataToSend)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Error al registrar información de sostenibilidad');
      }

      setMessage({ type: 'success', text: 'Información de sostenibilidad registrada exitosamente' });
      
      if (onComplete) {
        setTimeout(() => {
          onComplete();
        }, 2000);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Error al registrar información de sostenibilidad' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SustainabilityContainer>
      <SustainabilityCard>
        <Header>
          <LogoIcon>
            <Leaf size={40} color="white" />
          </LogoIcon>
          <Title>Información de Sostenibilidad</Title>
          <Subtitle>Complete los datos básicos sobre el impacto ambiental de su operación</Subtitle>
        </Header>

        {message && (
          <Message type={message.type}>
            {message.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
            {message.text}
          </Message>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Checkbox No Aplica */}
          <Section style={{ background: noAplica ? '#fef3c7' : undefined, border: noAplica ? '2px solid #f59e0b' : undefined }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={noAplica}
                onChange={(e) => setNoAplica(e.target.checked)}
                style={{ width: '20px', height: '20px', accentColor: '#f59e0b' }}
              />
              <span style={{ fontSize: '16px', fontWeight: 600, color: noAplica ? '#b45309' : '#374151' }}>
                No aplica - La empresa no tiene información de sostenibilidad disponible
              </span>
            </label>
            {noAplica && (
              <p style={{ marginTop: '8px', fontSize: '14px', color: '#92400e' }}>
                Al marcar esta opción, se registrará que la información de sostenibilidad no aplica para su empresa.
              </p>
            )}
          </Section>

          <Section style={{ opacity: noAplica ? 0.5 : 1 }}>
            <SectionTitle>
              <Fuel size={20} />
              Consumo de Combustible
            </SectionTitle>
            <FormGrid>
              <InputGroup>
                <Label>Consumo anual de combustible (galones) {!noAplica && '*'}</Label>
                <Input
                  type="number"
                  name="consumo_galones"
                  value={formData.consumo_galones}
                  onChange={handleChange}
                  placeholder="Ej: 15000"
                  step="0.01"
                  required={!noAplica}
                  disabled={noAplica}
                />
              </InputGroup>
              <InputGroup>
                <Label>Tipo de combustible principal {!noAplica && '*'}</Label>
                <Select
                  name="tipo_combustible"
                  value={formData.tipo_combustible}
                  onChange={handleChange}
                  required={!noAplica}
                  disabled={noAplica}
                >
                  <option value="DIESEL">Diesel</option>
                  <option value="GASOLINA">Gasolina</option>
                  <option value="ELECTRICO">Eléctrico</option>
                  <option value="OTRO">Otro</option>
                </Select>
              </InputGroup>
            </FormGrid>
          </Section>

          <Section style={{ opacity: noAplica ? 0.5 : 1 }}>
            <SectionTitle>
              <Truck size={20} />
              Operación de Flota
            </SectionTitle>
            <FormGrid>
              <InputGroup>
                <Label>Kilometraje anual total (km) {!noAplica && '*'}</Label>
                <Input
                  type="number"
                  name="kilometraje_anual"
                  value={formData.kilometraje_anual}
                  onChange={handleChange}
                  placeholder="Ej: 500000"
                  step="0.01"
                  required={!noAplica}
                  disabled={noAplica}
                />
              </InputGroup>
              <InputGroup>
                <Label>Número de vehículos {!noAplica && '*'}</Label>
                <Input
                  type="number"
                  name="numero_vehiculos"
                  value={formData.numero_vehiculos}
                  onChange={handleChange}
                  placeholder="Ej: 25"
                  min="1"
                  required={!noAplica}
                  disabled={noAplica}
                />
              </InputGroup>
            </FormGrid>
          </Section>

          <Section style={{ opacity: noAplica ? 0.5 : 1 }}>
            <SectionTitle>
              <Calculator size={20} />
              Emisiones Calculadas
            </SectionTitle>
            <InputGroup>
              <Label>Emisiones CO2 anuales (toneladas) - Calculado automáticamente</Label>
              <Input
                type="number"
                name="emisiones_co2_anual"
                value={formData.emisiones_co2_anual}
                onChange={handleChange}
                placeholder="Se calcula automáticamente"
                step="0.001"
                readOnly
                style={{ backgroundColor: '#f9fafb' }}
              />
            </InputGroup>
          </Section>

          {calculatedResults && (
            <ResultsSection>
              <SectionTitle>
                <CheckCircle size={20} />
                Indicadores de Sostenibilidad
              </SectionTitle>
              <ResultItem>
                <ResultLabel>Emisiones por kilómetro:</ResultLabel>
                <ResultValue>
                  {calculatedResults.kpi_emisiones_por_km ? 
                    `${calculatedResults.kpi_emisiones_por_km.toFixed(3)} kg CO2/km` : 
                    'N/A'
                  }
                </ResultValue>
              </ResultItem>
              <ResultItem>
                <ResultLabel>Emisiones por vehículo:</ResultLabel>
                <ResultValue>
                  {calculatedResults.kpi_emisiones_por_vehiculo ? 
                    `${calculatedResults.kpi_emisiones_por_vehiculo.toFixed(2)} ton CO2/vehículo` : 
                    'N/A'
                  }
                </ResultValue>
              </ResultItem>
              <ResultItem>
                <ResultLabel>Puntaje de sostenibilidad:</ResultLabel>
                <ResultValue>
                  {calculatedResults.puntaje_sostenibilidad ? 
                    `${calculatedResults.puntaje_sostenibilidad}/100` : 
                    'N/A'
                  }
                </ResultValue>
              </ResultItem>
              <ResultItem>
                <ResultLabel>Clasificación:</ResultLabel>
                <ClassificationBadge classification={calculatedResults.clasificacion_sostenibilidad}>
                  {calculatedResults.clasificacion_sostenibilidad}
                </ClassificationBadge>
              </ResultItem>
            </ResultsSection>
          )}

          <ButtonGroup>
            <Button type="submit" disabled={isLoading} style={{ flex: 1 }}>
              {isLoading ? (
                'Guardando...'
              ) : (
                <>
                  <Save size={16} />
                  Guardar Información
                </>
              )}
            </Button>
          </ButtonGroup>
        </Form>
      </SustainabilityCard>
    </SustainabilityContainer>
  );
};

export default SustainabilityForm;
